/*
 * lum-product-variants
 * Turns the split-apart option selectors back into a Shopify variant.
 *
 * A combined option packs several dimensions into one Shopify option using " | ".
 * The UI shows one selector per dimension, so on every click we re-join the parts
 * belonging to the same option position, match the resulting option array against
 * the variant list, and write the result to every price, SKU and form input on the
 * page. Both the purchase block and the sticky card carry the same hooks, so a
 * single pass keeps them in sync and they can never submit different variants.
 */


(function () {
    const root = document.querySelector('[data-lum-options]');
    if (!root) return;

    const dataEl = document.querySelector('[data-lum-variants]');
    if (!dataEl) return;

    let variants = [];
    try {
        variants = JSON.parse(dataEl.textContent);
    } catch (err) {
        console.error('Variant data could not be parsed', err);
        return;
    }

    /* Quote-only is a product-level tag, emitted alongside the variant data. */
    let isQuoteOnlyProduct = false;
    const quoteFlagEl = document.querySelector('[data-lum-quote-only]');
    if (quoteFlagEl) {
        try {
            isQuoteOnlyProduct = JSON.parse(quoteFlagEl.textContent) === true;
        } catch (err) {
            isQuoteOnlyProduct = false;
        }
    }

    const SEP = root.dataset.sep || ' | ';
    let userInteracted = false;
    const groups = Array.from(root.querySelectorAll('[data-lum-option]'));

    /* Current sub-value of one selector, read from whichever node is marked selected. */
    function selectedValue(group) {
        const node = group.querySelector('.is-selected[data-option-value]');
        return node ? node.dataset.optionValue : null;
    }

    /* Re-join sub-selectors back into one string per Shopify option position. */
    function buildOptionArray() {
        const byPosition = {};
        let complete = true;

        groups.forEach(function (group) {
            const position = parseInt(group.dataset.optionPosition, 10);
            const partIndex = parseInt(group.dataset.partIndex, 10);
            const value = selectedValue(group);
            if (value === null) complete = false;
            if (!byPosition[position]) byPosition[position] = [];
            byPosition[position][partIndex] = value;
        });

        if (!complete) return null;

        return Object.keys(byPosition)
            .sort(function (a, b) { return a - b; })
            .map(function (position) { return byPosition[position].join(SEP); });
    }

    function findVariant(optionArray) {
        if (!optionArray) return null;
        return variants.find(function (v) {
            return v.options.length === optionArray.length &&
                v.options.every(function (value, i) { return value === optionArray[i]; });
        }) || null;
    }

    /* Would this value still lead to an existing, in-stock variant? */
    function isReachable(group, candidate) {
        const position = parseInt(group.dataset.optionPosition, 10);
        const partIndex = parseInt(group.dataset.partIndex, 10);

        return variants.some(function (v) {
            if (!v.available) return false;

            return groups.every(function (other) {
                const otherPosition = parseInt(other.dataset.optionPosition, 10);
                const otherPartIndex = parseInt(other.dataset.partIndex, 10);
                const otherValue = other === group ? candidate : selectedValue(other);
                if (otherValue === null) return true;

                const full = v.options[otherPosition - 1];
                if (full === undefined) return false;
                const bits = full.split(SEP);
                return bits[otherPartIndex] === otherValue;
            });
        });
    }

    function markUnavailable() {
        groups.forEach(function (group) {
            group.querySelectorAll('[data-option-value]').forEach(function (node) {
                const reachable = isReachable(group, node.dataset.optionValue);
                node.classList.toggle('is-unavailable-card', !reachable);
                if (node.tagName === 'BUTTON') node.disabled = !reachable;
            });
        });
    }

    function updateSelectTrigger(group, value) {
        const trigger = group.querySelector('.lum-select__value');
        if (trigger) trigger.textContent = value;
    }



    /*
   * Gallery follows the variant: slide the main gallery to the slide holding the
   * variant's image. Slides carry data-image-id from product.images, which shares
   * ids with variant.featured_image (featured_media ids are different ids).
   * Swiper exposes its instance on the element, so the front-end gallery script
   * is left untouched.
   */
    let galleryReady = false;

    function syncGallery(variant) {
        /* First pass is the page load: keep the hero image unless a variant was linked. */
        if (!galleryReady) {
            galleryReady = true;
            if (!new URL(window.location.href).searchParams.has('variant')) return;
        }
        moveGallery(variant, false);
    }

    function moveGallery(variant, retried) {
        if (!variant || !variant.image_id) return;

        const mainEl = document.querySelector('.lum-main-gallery');
        if (!mainEl) return;

        if (!mainEl.swiper) {
            /* The gallery script may not have initialised yet on first load. */
            if (!retried) {
                window.addEventListener('load', function () { moveGallery(variant, true); }, { once: true });
            }
            return;
        }

        const slide = mainEl.querySelector('.swiper-slide[data-image-id="' + variant.image_id + '"]');
        if (!slide) return;

        /* In loop mode Swiper tags real slides with their true index. */
        const index = slide.hasAttribute('data-swiper-slide-index')
            ? parseInt(slide.getAttribute('data-swiper-slide-index'), 10)
            : Array.prototype.indexOf.call(slide.parentElement.children, slide);

        const main = mainEl.swiper;
        if (main.params.loop) main.slideToLoop(index);
        else main.slideTo(index);

        const thumbsEl = document.querySelector('.lum-sub-gallery');
        if (thumbsEl && thumbsEl.swiper) thumbsEl.swiper.slideTo(index);
    }



    function applyVariant(variant) {

        syncGallery(variant);

        /* Quote-only is a product-level tag, so it does not change with the variant. */
        const quoteOnly = isQuoteOnlyProduct;

        document.querySelectorAll('[data-variant-price]').forEach(function (el) {
            if (!variant) {
                el.textContent = '';
            } else if (quoteOnly) {
                el.textContent = 'Request a Quote';
            } else {
                el.textContent = variant.price;
            }
        });

        document.querySelectorAll('[data-variant-compare]').forEach(function (el) {
            if (variant && variant.compare_at) {
                el.textContent = variant.compare_at;
                el.hidden = false;
            } else {
                el.hidden = true;
            }
        });

        document.querySelectorAll('[data-variant-sku]').forEach(function (el) {
            el.textContent = variant && variant.sku ? variant.sku : '';
        });
        document.querySelectorAll('[data-variant-stock]').forEach(function (el) {
            /* A product-level shipping status set in the admin wins over the stock line */
            var override = el.getAttribute('data-stock-override');
            if (override) {
                el.textContent = override;
                return;
            }
            el.textContent = variant && variant.available ? '' : 'Out of Stock';
        });
        /* The hidden id is what actually gets added to the cart; never leave it stale. */
        document.querySelectorAll('[data-variant-id]').forEach(function (el) {
            el.value = variant ? variant.id : '';
        });


        const available = !!(variant && variant.available);
        document.querySelectorAll('[data-variant-submit]').forEach(function (button) {
            /* Quote-only variants have no price, so buying them directly is blocked. */
            button.classList.toggle('hidden', quoteOnly);
            button.disabled = quoteOnly || !available;
            const label = button.querySelector('[data-variant-submit-text]');
            if (label) label.textContent = available ? 'Add To Cart' : 'Sold Out';
        });

        /* Keep the URL shareable without reloading the page. */
        if (userInteracted && variant && window.history.replaceState) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url);
        }
    }

    function refresh() {
        /* No selectors on the page means a single-variant product; the server-rendered
           price, SKU and form are already correct, so leave them alone. */
        if (groups.length === 0) return;

        markUnavailable();
        applyVariant(findVariant(buildOptionArray()));
    }

    root.addEventListener('click', function (event) {
        const node = event.target.closest('[data-option-value]');
        if (!node) return;

        const group = node.closest('[data-lum-option]');
        if (!group || node.classList.contains('is-unavailable-card')) return;

        group.querySelectorAll('[data-option-value]').forEach(function (other) {
            other.classList.toggle('is-selected', other === node);
        });

        updateSelectTrigger(group, node.dataset.optionValue);
        userInteracted = true;
        refresh();
    });

    refresh();

    /*
   * Colour modal. Only the confirm button commits a choice, so the shopper can
   * browse without the page changing under them. Confirming forwards the value to
   * the matching inline swatch, which keeps one code path for variant switching.
   */
    const colorModal = document.querySelector('[data-color-modal]');
    if (colorModal && colorModal.parentElement !== document.body) {
        /* Move the modal out of the product content so position:fixed is never
           trapped by an ancestor with overflow or transform. */
        document.body.appendChild(colorModal);
    }
    if (colorModal) {
        let pendingValue = null;
        let pendingName = null;

        const label = colorModal.querySelector('[data-color-modal-label]');
        const confirm = colorModal.querySelector('[data-color-modal-confirm]');

        colorModal.addEventListener('click', function (event) {
            const swatch = event.target.closest('[data-color-modal-value]');
            if (!swatch || swatch.disabled) return;

            pendingValue = swatch.dataset.colorModalValue;
            pendingName = swatch.dataset.colorModalName || pendingValue;

            colorModal.querySelectorAll('[data-color-modal-value]').forEach(function (other) {
                other.classList.toggle('is-selected', other === swatch);
            });

            if (label) label.textContent = pendingName;
        });

        if (confirm) {
            confirm.addEventListener('click', function () {
                if (pendingValue === null) {
                    closeModal();
                    return;
                }

                /* Find the inline swatch for this colour and click it, so selection,
                   availability and variant lookup all run through the normal path. */
                const target = root.querySelector('[data-option-value="' + cssEscape(pendingValue) + '"]');
                if (target) {
                    target.click();
                } else {
                    /* Colour is beyond the inline limit. Rather than adding a hidden node,
                       repurpose the last inline swatch so the row keeps its fixed length. */
                    const group = Array.from(groups).find(function (g) {
                        return g.dataset.partName === 'Color';
                    });
                    if (group) {
                        const swatches = group.querySelectorAll('[data-option-value]');
                        const last = swatches[swatches.length - 1];
                        if (last) {
                            last.dataset.optionValue = pendingValue;
                            last.title = pendingName;
                            const source = colorModal.querySelector('[data-color-modal-value="' + cssEscape(pendingValue) + '"]');
                            if (source) last.style.cssText = source.style.cssText;
                            swatches.forEach(function (node) {
                                node.classList.toggle('is-selected', node === last);
                            });
                            refresh();
                        }
                    }
                }

                closeModal();
            });
        }

        function closeModal() {
            const closer = colorModal.querySelector('[data-modal-close]');
            if (closer) closer.click();
        }

        /* Values can contain quotes and spaces; escape before using in a selector. */
        function cssEscape(value) {
            return value.replace(/["\\]/g, '\\$&');
        }
    }

    /*
   * Request a Quote. The RFQ app renders its own button in the theme's app block
   * area; we keep it in the DOM but hidden, and forward clicks from the designed
   * buttons so the app's own flow runs unchanged.
   */
    const quoteTriggers = document.querySelectorAll('[data-lum-quote-trigger]');
    if (quoteTriggers.length) {
        const appButton = document.querySelector('.mprfq-add-to-quote-button-pdp');

        if (appButton) {
            quoteTriggers.forEach(function (trigger) {
                trigger.hidden = false;
                trigger.addEventListener('click', function () {
                    appButton.click();
                });
            });
        }
    }
})();